import React from 'react';
import {withRouter} from "react-router";
import {connect, useSelector} from "react-redux";
import {API} from "aws-amplify";
import {toast} from "react-toastify";
import _ from "lodash";
import {FormControl, InputLabel, Select, MenuItem} from "@mui/material";

import Modal from "@/common/Modal";
import ConfirmModal from "@/common/ConfirmModal";
import {deleteRule, fetchAllRules, setRulesFilter} from "@/shared/rules/actions";

import AddEditRule from "./AddEditRule";

const mapStateToProps = state => ({
    rules: state.rules,
})

const mapDispatchToProps = dispatch => ({
    getRules : () => dispatch(fetchAllRules()),
    deleteRule : (id) => dispatch(deleteRule(id)),
    saveFilter: (search, encounterType) => dispatch(setRulesFilter(search, encounterType))
})

function RulesIndex(props) {
    const [open, setOpen] = React.useState(false);
    const [editOpen, setEditOpen] = React.useState(false);
    const [editRule, setEditRule] = React.useState(null);

    // Search state — initialised from Redux so filter survives navigation
    const savedFilter = props.rules.rulesFilter || {};
    const [search, setSearch] = React.useState(savedFilter.search || '');
    const [searchFocused, setSearchFocused] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState(savedFilter.search || '');

    // Encounter type filter state — initialised from Redux
    const [encounterType, setEncounterType] = React.useState(savedFilter.encounterType || '');

    const searchRef = React.useRef();
    const [statusMessage, setStatusMessage] = React.useState('');

    const announce = React.useCallback((message) => {
        setStatusMessage('');
        setTimeout(() => setStatusMessage(message), 50);
    }, []);

    // Feature flags
    const features = useSelector(state => state.user?.features);
    const has311Feature = features?.some(f => f.feature === '311_ENCOUNTERS') ?? false;

    React.useEffect(() => {
        props.getRules();
        return () => {
            if (!props.history.location.pathname.startsWith('/rules/')) {
                props.saveFilter('', '');
            }
        };
    }, [])

    const onSuccess = (rule) => {
        props.getRules();
        setOpen(false);
        setEditOpen(false);
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
    }

    const handleEditRule = (rule) => {
        setEditRule(rule);
        setEditOpen(true);
    }

    const handleEditClose = () => {
        setEditRule(null);
        setEditOpen(false);
    }

    const editRuleVersions = (rule) => {
        props.history.push("/rules/" + rule.id);
    }

    const deleteRule = (rule) => {
        props.deleteRule(rule.id);
    }

    const toggleRule = (rule) => {
        let newStatus = rule.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
        API.put('Core', '/api/v1/rule/' + rule.id, {body: {status: newStatus}})
            .then(response => {
                    props.getRules();
                },
                error => {
                    toast("Could not change rule status: " + error.response.data.message, {position: "top-center", type: "error"});
                }
            );
    }

    const ENCOUNTER_TYPE_LABELS = { CAD: 'CAD', RMS_CASE: 'Records - Case', THREE_ONE_ONE: '311' };

    const getFilteredCount = (searchVal, encounterTypeVal) => {
        const trimmed = searchVal.trim().toLowerCase();
        let result = props.rules.rules || [];
        if (trimmed) result = result.filter(r => r.name.toLowerCase().includes(trimmed));
        if (encounterTypeVal) result = result.filter(r => r.activeRuleVersion?.encounterType === encounterTypeVal);
        return result.length;
    };

    const keyPress = (e) => {
        if (e.key === 'Enter') {
            const val = searchRef.current.value.trim();
            setSearch(val);
            setSearchInput(val);
            props.saveFilter(val, encounterType);
            const count = getFilteredCount(val, encounterType);
            announce(`Search completed. ${count} results displayed.`);
        }
    };

    const handleEncounterTypeChange = (e) => {
        const val = e.target.value;
        setEncounterType(val);
        props.saveFilter(search, val);
        const count = getFilteredCount(search, val);
        const label = ENCOUNTER_TYPE_LABELS[val];
        const msg = label
            ? `Encounter type ${label} applied. ${count} results displayed.`
            : `Encounter type filter cleared. ${count} results displayed.`;
        announce(msg);
    };

    // F4 — Filtering and sorting (memoized to avoid re-running on unrelated renders)
    const { activeRules, inactiveRules, totalCount } = React.useMemo(() => {
        const trimmedSearch = search.trim().toLowerCase();
        let result = props.rules.rules || [];
        if (trimmedSearch) {
            result = result.filter(r => r.name.toLowerCase().includes(trimmedSearch));
        }
        if (encounterType) {
            result = result.filter(r => r.activeRuleVersion?.encounterType === encounterType);
        }
        result = _.sortBy(result, r => r.name.toLowerCase());
        return {
            activeRules: result.filter(r => r.status === 'ACTIVE'),
            inactiveRules: result.filter(r => r.status !== 'ACTIVE'),
            totalCount: result.length,
        };
    }, [props.rules.rules, search, encounterType]);

    return (
        <div className="rootView rulesView">
            <div className="navBar">
                <div className="navTitle">
                    <div className="title">Rules</div>
                    <div className="counterBadge">
                        <div className="counterValue">{totalCount}</div>
                    </div>
                </div>
                <div className="actions">
                    {/* F3 — Rule Name Search */}
                    <div className={`searchInput action ${searchFocused ? "isFocused" : ""} ${searchInput.length > 0 ? "hasInput" : ""}`} style={{ marginRight: 10, backgroundColor: 'var(--colorBackgroundPrimarySub)' }}>
                        <div className="icon" />
                        <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Rule Name"
                            onFocus={() => setSearchFocused(true)}
                            onBlur={() => setSearchFocused(false)}
                            className="input"
                            onKeyDown={keyPress}
                            type="text"
                            ref={searchRef}
                            aria-label="Rule Name search"
                        />
                        <div
                            className="clear"
                            aria-label="Clear rule name search"
                            role="button"
                            tabIndex={searchInput.length > 0 ? 0 : -1}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSearch(''); setSearchInput(''); props.saveFilter('', encounterType); const count = getFilteredCount('', encounterType); announce(`Search cleared. ${count} results displayed.`); } }}
                            onClick={() => { setSearch(''); setSearchInput(''); props.saveFilter('', encounterType); const count = getFilteredCount('', encounterType); announce(`Search cleared. ${count} results displayed.`); }}
                        />
                    </div>

                    {/* F2 — Encounter Type Filter */}
                    <FormControl
                        variant="outlined"
                        size="small"
                        sx={{
                            width: 184,
                            backgroundColor: 'var(--colorBackgroundPrimarySub)',
                            color: 'var(--colorTitle)',
                            borderRadius: '5px',
                            '& .MuiInputBase-root': { height: 32 },
                            '& .MuiSelect-select': { paddingTop: '4px', paddingBottom: '4px' },
                            '& .MuiInputLabel-root': { color: 'var(--colorTitle)', opacity: 0.8 },
                            '& .MuiInputLabel-root.MuiInputLabel-shrink': { opacity: 1 },
                            '& .MuiInputLabel-root.Mui-focused': { color: 'var(--colorTitle)' },
                        }}
                    >
                        <InputLabel id="encounter-type-label" shrink>Encounter Type</InputLabel>
                        <Select
                            labelId="encounter-type-label"
                            label="Encounter Type"
                            value={encounterType}
                            onChange={handleEncounterTypeChange}
                            displayEmpty
                            notched
                            inputProps={{ 'aria-label': 'Encounter Type filter' }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        '& .MuiMenuItem-root:hover': { backgroundColor: 'var(--colorTintTranslucentHover)', color: 'var(--colorTitle)' },
                                        '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'var(--colorTint)', color: 'var(--colorTitleSecondary)' },
                                        '& .MuiMenuItem-root.Mui-selected:hover': { backgroundColor: 'var(--colorTint)', color: 'var(--colorTitleSecondary)' },
                                    }
                                }
                            }}
                        >
                            <MenuItem value="">ALL</MenuItem>
                            <MenuItem value="CAD">CAD</MenuItem>
                            <MenuItem value="RMS_CASE">Records - Case</MenuItem>
                            {has311Feature && (
                                <MenuItem value="THREE_ONE_ONE">311</MenuItem>
                            )}
                        </Select>
                    </FormControl>

                    <div className="">
                        <Modal
                            button={(<div className="add action">
                                <div className="icon"/>
                                <div className="title">Add Rule</div>
                            </div>)}
                            content={(<AddEditRule
                                onSuccess={onSuccess}
                                onCancel={handleClose}
                                rules={props.rules.rules}
                            />)}
                            title="Add Rule"
                            size="medium"
                            handleClose={handleClose}
                            handleOpen={handleOpen}
                            open={open}
                        />
                    </div>
                </div>
                <div className="separator"></div>
            </div>
            <div className="tableView rulesList">
                <div className="tableSection activeRules">
                    <div className="sectionTitle">Active Rules</div>
                    {
                        _.map(activeRules, function (rule) {
                            return (
                                <div className="tableCell ruleCell" key={rule.id}>
                                    <div className="cellBody" onClick={editRuleVersions.bind(this, rule)}>
                                        <div className="title ruleName">{(rule.name.length > 28 ? rule.name.substring(0, 28) + '...' : rule.name)}</div>
                                        <div className="subtitle version">{rule.activeRuleVersion ? "Active Version: " + rule.activeRuleVersion.version : "No Active Version"}</div>
                                    </div>

                                    <div className="ruleActions">
                                        <div className="hiddenActions">
                                            <div onClick={editRuleVersions.bind(this, rule)} className="ruleAction button small action">
                                                <div className="buttonTitle">Edit</div>
                                            </div>

                                            <ConfirmModal
                                                title={"Delete"}
                                                text={"Are you sure you want to delete this rule?"}
                                                onConfirm={deleteRule.bind(this, rule)}
                                                confirmTitle={"Delete Rule"}
                                            >
                                                <div className="ruleAction button destructive small">
                                                    <div className="title">Delete</div>
                                                </div>
                                            </ConfirmModal>
                                        </div>

                                        <ConfirmModal
                                            title={"Disable"}
                                            text={"Are you sure you want to disable this rule?"}
                                            onConfirm={toggleRule.bind(this, rule)}
                                            confirmTitle={"Disable Rule"}
                                        >
                                            <div className="ruleAction button action small">
                                                <div className="buttonTitle">Disable</div>
                                            </div>
                                        </ConfirmModal>
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
                <div className="tableSection inactiveRules">
                    <div className="sectionTitle">Inactive Rules</div>
                    {
                        _.map(inactiveRules, function (rule) {
                            return (
                                <div className="tableCell ruleCell" key={rule.id}>
                                    <div className="cellBody" onClick={editRuleVersions.bind(this, rule)}>
                                        <div className="title ruleName">{(rule.name.length > 28 ? rule.name.substring(0, 28) + '...' : rule.name)}</div>
                                        <div className="subtitle version">{rule.activeRuleVersion ? "Active Version: " + rule.activeRuleVersion.version : "No Active Versions"}</div>
                                    </div>

                                    <div className="ruleActions">
                                        <div className="hiddenActions">
                                            <div onClick={editRuleVersions.bind(this, rule)} className="ruleAction button small action">
                                                <div className="buttonTitle">Edit</div>
                                            </div>

                                            <ConfirmModal
                                                title={"Delete"}
                                                text={"Are you sure you want to delete this rule?"}
                                                onConfirm={deleteRule.bind(this, rule)}
                                                confirmTitle={"Delete Rule"}
                                            >
                                                <div className="ruleAction button destructive small">
                                                    <div className="title">Delete</div>
                                                </div>
                                            </ConfirmModal>
                                        </div>

                                        <ConfirmModal
                                            title={"Set Active"}
                                            text={"Are you sure you want to enable this rule to start sending?"}
                                            onConfirm={toggleRule.bind(this, rule)}
                                            confirmTitle={"Enable Rule"}
                                        >
                                            <div className="ruleAction button tint small">
                                                <div className="title">Activate</div>
                                            </div>
                                        </ConfirmModal>
                                    </div>
                                </div>
                            )
                        })
                    }
                </div>
            </div>
            <Modal
                button={null}
                content={(<AddEditRule
                    existingRule={editRule}
                    onSuccess={onSuccess}
                    onCancel={handleEditClose}
                />)}
                title="Edit Rule"
                size="small"
                handleClose={handleEditClose}
                open={editOpen}
            />

            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}
            >
                {statusMessage}
            </div>
        </div>
    )
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RulesIndex));
